import { ChatOpenAI } from "@langchain/openai";
import { StringOutputParser } from "@langchain/core/output_parsers";
import { PromptTemplate } from "@langchain/core/prompts";
import { runEditorReview } from "./editorAgents";
import { parseAiJson } from "./aiJson";
import { getChatModel } from "./llmClient";
import { getAuthHeader } from "../../lib/authFetch";

async function fetchProjectDocsText(projectId) {
  if (!projectId) return "";

  try {
    const res = await fetch(`/api/projects/${projectId}/documents`, {
      headers: await getAuthHeader(),
    });
    const json = await res.json();

    if (!res.ok) return "";

    // Expecting: { ok: true, documents: [...] }
    const docs = json?.documents || [];

    // Join extracted_text into one big context string
    const combined = docs
      .map((d) => {
        const title = d?.title || "Untitled document";
        const text = (d?.extracted_text || "").trim();
        if (!text) return "";
        return `--- ${title} ---\n${text}`;
      })
      .filter(Boolean)
      .join("\n\n");

    return combined;
  } catch (e) {
    console.error("Failed to load project docs text:", e);
    return "";
  }
}

function mapLengthBucketToWordCount(bucket) {
  switch (bucket) {
    case "<500":
      return 450;
    case "<1000":
      return 900;
    case "<1500":
      return 1300;
    case "<2000":
      return 1800;
    case "2000+":
      return 2200;
    default:
      return 1200;
  }
}

export const generateContent = async (
  formData,
  setFeedback,
  onProgress,
  provider = "openai"
) => {
  onProgress?.("Generating your article...");
  const llm = await getChatModel(provider);

  let results;

  // --- pull the new fields from your wizard modal payload ---
  const {
    // Step 1
    title,
    purpose, // array of strings
    // Step 2
    primaryAudience,
    secondaryAudience,
    audienceKnowledgeLevel,
    // Step 3
    articleLength, // "<500" | "<1000" | "<1500" | "<2000" | "2000+"
    tone, // array of strings
    // Step 4
    primaryKeywordsRaw,
    secondaryKeywordsRaw,
    // Step 5
    referenceUrlsRaw,
    keyMessagesRaw,
    commonMistakesRaw,
    // Step 8
    additionalNotes,
    uploadedBriefText,
    projectId,
  } = formData;

  const projectDocsText = await fetchProjectDocsText(projectId);

  const wordCount = articleLength;

  const format = `{
    "Article": <String>,
    "Meta Tags": <head>
      <title>...</title>
      <meta name="description" content="...">
      <meta name="keywords" content="...">
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <link rel="canonical" href="...">
      <meta property="og:title" content="...">
      <meta property="og:description" content="...">
      <meta property="og:image" content="...">
      <meta property="og:url" content="...">
      <meta name="twitter:card" content="summary_large_image">
      <meta name="twitter:title" content="...">
      <meta name="twitter:description" content="...">
      <meta name="twitter:image" content="...">
      <meta name="robots" content="index, follow">
    </head>
  }`;

  const promptTemplate = `You are a bot that specializes in creating article/blog content for a blog.
Return ONLY a single JSON object that matches the exact 'Format' provided. Do not include triple backticks.

Write an SEO-optimized HTML article using the details below. Optimize headings, structure, internal logic, and meta elements. Use clear, concise HTML (no stray newlines for spacing—HTML handles layout). Target word count is a guideline, not a hard cap.

Scoring goals: Overall, Readability, Topic Authority, Content Value, and SEO.
Meta Tags: Return a valid <head> block as a STRING in the "Meta Tags" property. Keep lengths optimal.

Inputs:
- Working Title: {title}
- Purpose/Goal: {purpose}
- Primary Audience: {primaryAudience}
- Secondary Audience: {secondaryAudience}
- Audience Knowledge Level: {audienceKnowledgeLevel}
- Target Word Count: {wordCount}
- Tone & Voice: {tone}
- Primary Keywords (comma-separated): {primaryKeywordsRaw}
- Secondary Keywords (comma-separated): {secondaryKeywordsRaw}
- Reference URLs for Article Content and Format (comma-separated urls): {referenceUrlsRaw}
- Key Messages / Takeaways (comma-separated): {keyMessagesRaw}
- Common Mistakes / Misconceptions / Rules (comma-separated, whitelist/blacklist style): {commonMistakesRaw}
- Additional Notes: {additionalNotes}
- Source Brief (if provided): {uploadedBriefText}
- Project Guidelines / PDFs (if provided): {projectDocsText}

Authoring guidance:
- Align depth and jargon with the audience knowledge level.
- Bulleted lists are fine but should be used very sparingly.
- Follow the reference URLs & key messages closely; weave keywords naturally (no stuffing).
- Use semantic HTML structure (<article>, headings hierarchy).
- Where relevant, propose internal link anchor ideas as HTML comments (e.g., <!-- internal: anchor idea -->).
- Include practical examples, brief checklists, or steps when useful.
- Do not wrap the JSON in backticks.

Format: {format}`;

  const prompt = PromptTemplate.fromTemplate(promptTemplate);

  const chain = prompt.pipe(llm).pipe(new StringOutputParser());

  const inputs = {
    format,
    title,
    purpose: (purpose || []).join(", "),
    primaryAudience,
    secondaryAudience,
    audienceKnowledgeLevel,
    wordCount,
    tone: (tone || []).join(", "),
    primaryKeywordsRaw,
    secondaryKeywordsRaw,
    referenceUrlsRaw,
    keyMessagesRaw,
    commonMistakesRaw,
    additionalNotes,
    uploadedBriefText: uploadedBriefText || "",
    projectDocsText: projectDocsText || "",
  };

  console.log("Prompt variables being sent to ChatGPT:", inputs);

  const renderedPrompt = await prompt.format(inputs);
  console.log("Rendered prompt string:\n", renderedPrompt);

  const response = await chain.invoke(inputs);

  results = parseAiJson(response);
  console.log("AI Response: ", results);

  // Add a newline after every "</title>" symbol
  let metaTagStr = results["Meta Tags"].replace(/<\/title>/g, "</title>\n");
  // Add a newline after every ">" symbol for other tags, except <title> and </title>
  metaTagStr = metaTagStr.replace(/(?<!<\/?title)>/g, ">\n");

  const edited = await runEditorReview(
    results["Article"],
    metaTagStr,
    inputs,
    onProgress,
    provider
  );
  results["Article"] = edited.articleHtml;
  metaTagStr = edited.metaTags;

  setFeedback({
    overall: 0,
    readability: 0,
    topicAuthority: 0,
    contentValue: 0,
    seo: 0,
    metaTags: metaTagStr,
  });

  return results;
};

export const reviseSelection = async ({
  selectedText,
  instruction,
  contextHtml,
}) => {
  const OPENAI_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:5000/openai/v1"
      : `${window.location.origin}/openai/v1`;
  console.log("Using OpenAI baseURL Revision:", OPENAI_BASE);

  const llm = new ChatOpenAI({
    openAIApiKey: "proxied",
    temperature: 1,
    modelName: "gpt-5",
    configuration: { baseURL: OPENAI_BASE, defaultHeaders: await getAuthHeader() },
  });

  const format = `{
    "revised_html": <String>
  }`;

  const promptTemplate = `You are an expert editor.
Return ONLY a single JSON object that matches the exact 'Format' provided. Do not include triple backticks.

Task:
- Rewrite ONLY the user's selected section based on the instruction.
- Keep the meaning consistent unless the instruction requests otherwise.
- Output must be valid HTML (no markdown).

Instruction: {instruction}

Selected section (plain text or HTML):
{selectedText}

Full article HTML context (for tone + consistency; do not rewrite this):
{contextHtml}

Format: {format}`;

  const prompt = PromptTemplate.fromTemplate(promptTemplate);
  const chain = prompt.pipe(llm).pipe(new StringOutputParser());

  const inputs = { format, instruction, selectedText, contextHtml };
  console.log("Prompt variables being sent to ChatGPT:", inputs);

  const renderedPrompt = await prompt.format(inputs);
  console.log("Rendered prompt string:\n", renderedPrompt);

  const response = await chain.invoke({
    format,
    instruction,
    selectedText,
    contextHtml,
  });

  const parsed = parseAiJson(response);

  return parsed.revised_html || "";
};
